/**
 * Scroll-reveal for the Alpha rebrand.
 *
 * The governing rule here is that CONTENT MUST NEVER STAY INVISIBLE. The reveal
 * classes start at opacity 0, so any path where the observer does not fire hides
 * real merchandise from a customer. An earlier version relied solely on
 * IntersectionObserver and did exactly that - 17 elements marked for reveal, none
 * of them ever marked as seen, half the homepage blank.
 *
 * So there are three independent ways an element gets revealed, in order:
 *   1. On init, anything already at or near the viewport is revealed outright,
 *      measured with getBoundingClientRect rather than waiting for a callback.
 *   2. IntersectionObserver handles everything further down the page.
 *   3. A failsafe. If the observer never fired at all it is broken or unsupported,
 *      and everything is revealed outright - the page degrades to "no animation"
 *      instead of "no content". If the observer IS working, the failsafe only
 *      sweeps what is already on screen, because blanket-revealing the document
 *      would pre-reveal every section below the fold and scrolling would animate
 *      nothing. That regression is exactly what this balance exists to avoid.
 */

(function () {
  const SELECTOR = '.alpha-reveal, .alpha-stagger';
  const SEEN = 'is-in';

  const reveal = function (el) {
    if (el && !el.classList.contains(SEEN)) el.classList.add(SEEN);
  };

  const revealAll = function (root) {
    (root || document).querySelectorAll(SELECTOR).forEach(reveal);
  };

  // Reveal only what the visitor can already see or has scrolled past. This is
  // the safety sweep: it closes holes in the page without pre-revealing anything
  // still below the fold, which is what keeps scrolling animated.
  const sweepVisible = function () {
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    document.querySelectorAll(SELECTOR).forEach(function (el) {
      if (el.classList.contains(SEEN)) return;
      /*
       * DELIBERATELY LESS EAGER THAN THE OBSERVER.
       *
       * This is a safety net for elements the observer missed, not a second
       * trigger. At `rect.top < vh` it fired the moment an element's top edge
       * touched the bottom of the viewport - earlier than the observer, which
       * waits for vh * 0.88 - so on every scroll the sweep won the race and
       * revealed everything before it came into view. The page looked static for
       * the same reason the blanket failsafe used to make it look static.
       *
       * At 0.4 an element has to be nearly half way up the screen before the net
       * catches it, well after the observer should have fired. If the observer
       * is working, this never does anything.
       */
      if (el.getBoundingClientRect().top < vh * 0.4) reveal(el);
    });
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const start = function () {
    if (reducedMotion || !('IntersectionObserver' in window)) {
      revealAll(document);
      return;
    }

    // Proof that the observer is alive. The failsafe below is a recovery path for
    // a BROKEN observer, and it used to fire unconditionally - blanket-revealing
    // the whole document 2.5s after load. That guaranteed content was visible,
    // but it also meant every section below the fold had already been revealed
    // before the visitor scrolled to it, so scrolling animated nothing at all.
    let observerFired = false;

    const observer = new IntersectionObserver(
      function (entries) {
        // ANY callback proves the observer is alive - including the initial one
        // that reports everything as NOT intersecting. Setting this only on an
        // intersection meant that loading the page and not scrolling left the
        // flag false, and the failsafe below then revealed the whole document at
        // 2.5s. Refresh, wait, scroll: nothing animated, ever.
        observerFired = true;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      // A low threshold matters for tall grids: requiring a large fraction of a
      // full-height element to be visible can mean it never qualifies at all.
      //
      // The bottom margin is what makes the animation VISIBLE. A positive top
      // margin of 200px used to fire the reveal while the element was still a
      // couple of hundred pixels below the fold, so it had finished animating
      // before it scrolled into view and the page looked static. Pulling the
      // bottom edge in by 12% instead means an element must be genuinely on
      // screen before it starts, and you watch it arrive.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
    );

    const attach = function (root) {
      (root || document).querySelectorAll(SELECTOR).forEach(function (el) {
        if (el.classList.contains(SEEN)) return;

        // Only what is genuinely on screen now. This used to be 1.25 viewports,
        // which pre-revealed the whole next screenful and meant the first scroll
        // of the page was completely static.
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        if (rect.top < vh * 0.9) {
          reveal(el);
          return;
        }

        observer.observe(el);
      });
    };

    attach(document);

    document.addEventListener('shopify:section:load', function (event) {
      attach(event.target);
    });

    new MutationObserver(function (records) {
      records.forEach(function (r) {
        r.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches(SELECTOR)) attach(n.parentNode || document);
          attach(n);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });

    // Failsafe, but a TARGETED one.
    //
    // If the observer has fired at least once it is working, and blanket-revealing
    // would just destroy the scroll animation for everything further down. In that
    // case sweep only what the visitor can already see or has scrolled past - those
    // are the elements where a missed callback would show as a hole in the page.
    //
    // If the observer never fired AT ALL - not even the initial callback - it is
    // broken, and the old guarantee applies: reveal everything, because a missing
    // animation is far cheaper than a missing product grid. In practice this now
    // only happens if the constructor succeeded but the browser never scheduled
    // the first callback, which is close to never.
    /*
     * The failsafe must not run while the tab is HIDDEN.
     *
     * A background tab stops delivering IntersectionObserver callbacks, so
     * `observerFired` stays false through no fault of the observer - and the
     * recovery path then blanket-revealed the whole document. The real-world
     * sequence was: load the page, switch to another tab or app for a few
     * seconds, come back, scroll, and nothing animates because everything was
     * already revealed while you were away.
     *
     * Nobody can see a hole in a page they are not looking at, so there is
     * nothing to recover from until the page is visible. The countdown restarts
     * from the moment it becomes visible.
     */
    const runFailsafe = function () {
      if (observerFired) {
        sweepVisible();
      } else {
        revealAll(document);
      }
    };

    const armFailsafe = function () {
      window.clearTimeout(failsafeTimer);
      failsafeTimer = window.setTimeout(function () {
        if (document.hidden) return;
        runFailsafe();
      }, 2500);
    };

    let failsafeTimer = null;
    armFailsafe();

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) armFailsafe();
    });

    // A late safety net for the case where the observer fired early, then stopped
    // (a detached observer after a section re-render, for example). Anything the
    // visitor has actually scrolled to and which is still hidden gets revealed.
    let sweepQueued = false;
    window.addEventListener(
      'scroll',
      function () {
        if (sweepQueued) return;
        sweepQueued = true;
        window.requestAnimationFrame(function () {
          sweepQueued = false;
          sweepVisible();
        });
      },
      { passive: true }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Whatever happens above, nothing the visitor can SEE stays hidden past load.
  // Deliberately not revealAll: that would pre-reveal the entire page and the
  // scroll animations would never play.
  window.addEventListener('load', function () {
    window.setTimeout(function () {
      sweepVisible();
    }, 1200);
  });
})();
